/**
 * Cohort Management Component
 * Admin view for managing cohorts and student assignments.
 */

import { EmptyState } from '../../components/empty-state';
import { SearchInput } from '../../components/search-input';
import { Modal } from '../../components/modal';
import { Table } from '../../components/table';
import { CheckRow, Checkbox, FormField, FormMessage, Input, Select, Textarea } from '../../components/form-field';
import { saveFailure, loadFailure, deleteFailure, type FormFailure } from './save-failure-words';
import { useMessageInSight } from '../../components/use-message-in-sight';
import { activeTone } from './status-tone';
import { Pill } from '../../components/pill';
import { useMemo, useState } from 'react';
import { Button } from '../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../shell/common-header';
import type { CohortItem, CohortWithStudents } from '../../api/admin/cohorts/types';
import type { UserListItem } from '../../api/admin/users/types';
import {
  useAdminCohorts,
  useAdminCohort,
  useCreateCohort,
  useUpdateCohort,
  useDeleteCohort,
  useAddCohortStudents,
  useRemoveCohortStudents,
} from '../../api/admin/cohorts/hooks';
import { useAdminUsers } from '../../api/admin/users/hooks';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';
import admin from './admin-dashboard.module.css';

interface CohortManagementProps {
  onBack: () => void;
}

export function CohortManagement({ onBack }: CohortManagementProps) {
  const { t } = useTranslation();
  const cohortsQuery = useAdminCohorts();
  const deleteCohort = useDeleteCohort();
  const cohorts: CohortItem[] = cohortsQuery.data ?? [];
  const isLoading = cohortsQuery.isLoading;
  const [pageError, setPageError] = useState<FormFailure | null>(null);
  const queryError = cohortsQuery.isError
    ? loadFailure(t, cohortsQuery.error, 'cohorts')
    : null;
  const error = pageError ?? queryError;
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCohort, setEditingCohort] = useState<CohortItem | null>(null);
  const [managingCohortId, setManagingCohortId] = useState<string | null>(null);
  const managedCohortQuery = useAdminCohort(managingCohortId);
  const managingCohort = managedCohortQuery.data ?? null;
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('');

  const reload = () => { void cohortsQuery.refetch(); };

  const handleDelete = async (id: string, name: string) => {
    if (!(await confirmDialog({ message: t('admin.cohorts.confirmDelete', { name }), tone: 'danger' }))) return;
    setPageError(null);
    try {
      await deleteCohort.mutateAsync(id);
    } catch (e) {
      setPageError(deleteFailure(t, e, 'cohort'));
    }
  };

  const handleManageStudents = (cohort: CohortItem) => {
    setPageError(null);
    setManagingCohortId(cohort.id);
  };

  const q = searchText.toLowerCase();
  const filteredCohorts = cohorts.filter((c) => {
    const matchesSearch = !q ||
      c.name.toLowerCase().includes(q) ||
      (c.description ?? '').toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === '' ||
      (statusFilter === 'active' && c.isActive) ||
      (statusFilter === 'inactive' && !c.isActive);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="portal-shell">
      <CommonHeader
        title={t('admin.cohorts.title')}
        showBackButton
        onBackClick={onBack}
      />

      <main className={admin.adminPanelMain}>
        {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}

        <div className={admin.adminTableWrapper}>
          <div className={admin.adminTableHeader}>
            <h2 className={admin.adminTableTitle}>
              {t('admin.cohorts.totalCount', { count: cohorts.length })}
            </h2>
            <div className={admin.adminFilterBar}>
              <Select
                className={admin.adminFilter}
                aria-label={t('common.filter')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as '' | 'active' | 'inactive')}
              >
                <option value="">{t('common.filterAll')}</option>
                <option value="active">{t('common.filterActive')}</option>
                <option value="inactive">{t('common.filterInactive')}</option>
              </Select>
              <Button type="button" onClick={() => setShowCreateForm(true)}
              >
                + {t('admin.cohorts.createCohort')}
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className={admin.adminLoading}>{t('common.loading')}</div>
          ) : cohorts.length === 0 ? (
            !error && <EmptyState icon="🏫">{t('admin.cohorts.noCohorts')}</EmptyState>
          ) : (
            <>
              <div className={admin.adminSearchBar}>
                <SearchInput
            placeholder={t('common.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
                {searchText && (
                  <span className={admin.adminSearchResults}>
                    {t('common.searchResults', { count: filteredCohorts.length, total: cohorts.length })}
                  </span>
                )}
              </div>
              <Table flush>
              <thead>
                <tr>
                  <th>{t('admin.cohorts.columns.name')}</th>
                  <th>{t('admin.cohorts.columns.description')}</th>
                  <th>{t('admin.cohorts.columns.students')}</th>
                  <th>{t('admin.cohorts.columns.status')}</th>
                  <th>{t('admin.cohorts.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCohorts.map((cohort) => (
                  <tr key={cohort.id}>
                    <td className="font-medium">{cohort.name}</td>
                    <td>{cohort.description ?? '-'}</td>
                    <td>{cohort.studentCount}</td>
                    <td>
                      <Pill tone={activeTone(cohort.isActive)}>
                        {cohort.isActive ? t('admin.users.active') : t('admin.users.inactive')}
                      </Pill>
                    </td>
                    <td>
                      <div className={admin.adminRowActions}>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditingCohort(cohort)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleManageStudents(cohort)}
                        >
                          {t('admin.cohorts.manageStudents')}
                        </Button>
                        <Button type="button" variant="dangerQuiet" size="sm" onClick={() => handleDelete(cohort.id, cohort.name)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </>
          )}
        </div>
      </main>

      {showCreateForm && (
        <CohortForm
          onClose={() => setShowCreateForm(false)}
          onSaved={() => { setShowCreateForm(false); reload(); }}
        />
      )}

      {editingCohort && (
        <CohortForm
          cohort={editingCohort}
          onClose={() => setEditingCohort(null)}
          onSaved={() => { setEditingCohort(null); reload(); }}
        />
      )}

      {managingCohort && (
        <ManageStudentsPanel
          cohort={managingCohort}
          onClose={() => { setManagingCohortId(null); reload(); }}
        />
      )}
    </div>
  );
}

interface CohortFormProps {
  cohort?: CohortItem;
  onClose: () => void;
  onSaved: () => void;
}

function CohortForm({ cohort, onClose, onSaved }: CohortFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(cohort?.name ?? '');
  const [description, setDescription] = useState(cohort?.description ?? '');
  const [isActive, setIsActive] = useState(cohort?.isActive ?? true);
  const createCohort = useCreateCohort();
  const updateCohort = useUpdateCohort();
  const isSubmitting = createCohort.isPending || updateCohort.isPending;
  const [error, setError] = useState<FormFailure | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (cohort) {
        await updateCohort.mutateAsync({ id: cohort.id, payload: { name, description: description || undefined, isActive } });
      } else {
        await createCohort.mutateAsync({ name, description: description || undefined });
      }
      onSaved();
    } catch (e) {
      setError(saveFailure(t, e, 'cohort'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="mdWide" disableBackdropClose title={cohort ? t('admin.cohorts.editCohort') : t('admin.cohorts.createCohort')}>
      {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}
      <form onSubmit={handleSubmit}>
        <FormField label={t('admin.cohorts.form.name')} required>
          {(field) => (
            <Input {...field}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          )}
        </FormField>
        <FormField label={t('admin.cohorts.form.description')}>
          {(field) => (
            <Textarea {...field}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </FormField>
        {cohort && (
          <CheckRow spaced>
            <Checkbox
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>{t('admin.cohorts.form.active')}</span>
          </CheckRow>
        )}
        <div className={admin.adminFormActions}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface ManageStudentsPanelProps {
  cohort: CohortWithStudents;
  onClose: () => void;
}

function ManageStudentsPanel({ cohort, onClose }: ManageStudentsPanelProps) {
  const { t } = useTranslation();
  // Load all students (large page) via TanStack Query so the cache survives
  // when the panel is reopened.
  const studentsQuery = useAdminUsers(1, 500, 'STUDENT');
  const allStudents: UserListItem[] = studentsQuery.data?.data ?? [];
  const isLoading = studentsQuery.isLoading;
  const addStudents = useAddCohortStudents();
  const removeStudents = useRemoveCohortStudents();
  const isSaving = addStudents.isPending || removeStudents.isPending;
  const [currentStudentIds, setCurrentStudentIds] = useState<Set<string>>(
    new Set(cohort.students.map((s) => s.id)),
  );
  const [actionError, setError] = useState<FormFailure | null>(null);
  const queryError = useMemo<FormFailure | null>(() => studentsQuery.isError ? loadFailure(t, studentsQuery.error, 'students') : null, [studentsQuery.isError, studentsQuery.error, t]);
  const error = actionError ?? queryError;
  const { messageRef, actionsRef } = useMessageInSight(error);
  const [studentSearch, setStudentSearch] = useState('');
  const [memberFilter, setMemberFilter] = useState<'all' | 'in' | 'out'>('all');

  const toggleStudent = (studentProfileId: string) => {
    setCurrentStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentProfileId)) next.delete(studentProfileId);
      else next.add(studentProfileId);
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    try {
      const originalIds = new Set(cohort.students.map((s) => s.id));
      const toAdd = [...currentStudentIds].filter((id) => !originalIds.has(id));
      const toRemove = [...originalIds].filter((id) => !currentStudentIds.has(id));
      if (toAdd.length > 0) await addStudents.mutateAsync({ id: cohort.id, studentIds: toAdd });
      if (toRemove.length > 0) await removeStudents.mutateAsync({ id: cohort.id, studentIds: toRemove });
      onClose();
    } catch (e) {
      setError(saveFailure(t, e, 'cohortStudents'));
    }
  };

  const q = studentSearch.toLowerCase();
  const filteredStudents = allStudents.filter((s) => {
    const profileId = s.studentProfile?.id;
    if (!profileId) return false;
    const matchSearch = !q || s.firstName?.toLowerCase().includes(q) || s.lastName?.toLowerCase().includes(q) || s.name.toLowerCase().includes(q) || (s.email ?? '').toLowerCase().includes(q) || (s.username ?? '').toLowerCase().includes(q);
    const isMember = currentStudentIds.has(profileId);
    const matchFilter = memberFilter === 'all' || (memberFilter === 'in' && isMember) || (memberFilter === 'out' && !isMember);
    return matchSearch && matchFilter;
  });

  const addedCount = [...currentStudentIds].filter(id => !new Set(cohort.students.map(s => s.id)).has(id)).length;
  const removedCount = [...cohort.students.map(s => s.id)].filter(id => !currentStudentIds.has(id)).length;

  return (
    <Modal isOpen onClose={onClose} title={t('admin.cohorts.manageStudentsFor', { name: cohort.name })} size="lg">
      {/* Pending changes summary */}
      {(addedCount > 0 || removedCount > 0) && (
        <div className={admin.adminManageSummary}>
          {addedCount > 0 && <span className={admin.adminManageSummaryAdd}>+{addedCount} {t('admin.cohorts.toAdd')}</span>}
          {removedCount > 0 && <span className={admin.adminManageSummaryRemove}>−{removedCount} {t('admin.cohorts.toRemove')}</span>}
        </div>
      )}

      {/* Search + filter bar */}
      <div className={admin.adminManageSearchRow}>
        <SearchInput grow
            placeholder={t('admin.cohorts.searchStudents')}
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
          />
        <Select
          className={admin.adminFilter}
                aria-label={t('common.filter')}
          value={memberFilter}
          onChange={(e) => setMemberFilter(e.target.value as 'all' | 'in' | 'out')}
        >
          <option value="all">{t('admin.cohorts.filterAll')}</option>
          <option value="in">{t('admin.cohorts.filterMembers')}</option>
          <option value="out">{t('admin.cohorts.filterNonMembers')}</option>
        </Select>
      </div>
      <div className={admin.adminManageCount}>
        {t('admin.cohorts.showingStudents', { shown: filteredStudents.length, total: allStudents.length })}
      </div>

      {isLoading ? (
        <div className={admin.adminLoading}>{t('common.loading')}</div>
      ) : (
        <div className={`${admin.adminPickList} ${admin.adminPickListTall}`}>
          {filteredStudents.length === 0 ? (
            <EmptyState inline>{t('common.noResults')}</EmptyState>
          ) : filteredStudents.map((student) => {
            const profileId = student.studentProfile?.id;
            if (!profileId) return null;
            const isMember = currentStudentIds.has(profileId);
            return (
              <label
                key={student.id}
                className={`${admin.adminManageStudentRow} ${isMember ? admin.adminManageStudentRowIn : ''}`}
              >
                <Checkbox
                  checked={isMember}
                  onChange={() => toggleStudent(profileId)}
                  className="w-4 h-4"
                />
                <span className={admin.adminManageStudentInfo}>
                  <span className={admin.adminManageStudentName}>{student.firstName} {student.lastName}</span>
                  <span className={admin.adminManageStudentMeta}>{student.username ? `@${student.username} · ` : ''}{student.email}</span>
                </span>
                {isMember && (
                  <Pill solid tone="success">{t('admin.cohorts.member')}</Pill>
                )}
              </label>
            );
          })}
        </div>
      )}

      {/* Above Save, kept in sight as it comes (useMessageInSight): the form's top scrolls out of sight. */}
      {error && (
        <div ref={messageRef} tabIndex={-1} role="group" aria-label={error.title ?? error.detail ?? undefined}>
          <FormMessage title={error.title}>{error.detail}</FormMessage>
        </div>
      )}
      <div ref={actionsRef} className={admin.adminFormActions}>
        <Button type="button" variant="ghost" onClick={onClose} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={handleSave} disabled={isSaving || isLoading}>
          {isSaving ? t('common.loading') : t('common.save')}
        </Button>
      </div>
    </Modal>
  );
}
